import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingSection = () => {
  const [selectedPlan, setSelectedPlan] = useState('financiamiento');

  // UI/estado de envío
  const [errorMsg, setErrorMsg] = useState('');
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [sendingPlan, setSendingPlan] = useState(false);

  // Datos del interesado
  const [lead, setLead] = useState({ nombre: '', email: '', telefono: '' });
  const [planError, setPlanError] = useState('');

  const plans = [
    {
      id: 'contado',
      name: 'Pago al Contado',
      discount: '15% Descuento',
      price: 'S/ 15,300',
      originalPrice: 'desde S/ 18,000',
      monthlyEquivalent: 'Ahorra hasta S/ 2,700',
      features: [
        '🎉 Descuento especial del 15%',
        '📝 Escritura inmediata',
        '🚫 Sin intereses ni comisiones',
        '🏗️ Bonificación en acabados premium',
        '⚖️ Asesoría legal gratuita completa'
      ],
      recommended: false,
      icon: 'Wallet',
      badge: 'Mejor Precio',
      color: 'success'
    },
    {
      id: 'financiamiento',
      name: 'Financiamiento Directo',
      discount: 'Cuotas Flexibles',
      price: 'S/ 342',
      originalPrice: 'mensual',
      monthlyEquivalent: 'Inicial desde S/ 3,600 (20%)',
      features: [
        '💰 Inicial desde 20% flexible',
        '📅 Hasta 60 meses (5 años)',
        '📉 Tasa preferencial 8.5% anual',
        '⚡ Sin penalidad por prepago',
        '✅ Aprobación en 48 horas'
      ],
      recommended: true,
      icon: 'CreditCard',
      badge: 'Más Popular',
      color: 'primary'
    },
    {
      id: 'privado',
      name: 'Crédito Privado',
      discount: 'Hasta 5 Años',
      price: 'S/ 15,000',
      originalPrice: 'valor desde',
      monthlyEquivalent: 'Cuotas desde S/ 575/mes',
      features: [
        '🏦 Financiamiento privado directo',
        '📊 Asesoría financiera experta',
        '💸 Tasa competitiva 8.5% anual',
        '⏳ Hasta 60 meses plazo máximo',
        '📋 Separación desde S/ 300'
      ],
      recommended: false,
      icon: 'Building2',
      badge: 'Flexible',
      color: 'primary'
    }
  ];

  const lotSizes = [
    { size: '150 m²', price: 'S/ 18,000', monthly: 'S/ 575', available: 7, urgency: 'medium', description: 'Ideal para casa compacta' },
    { size: '165 m²', price: 'S/ 21,000', monthly: 'S/ 670', available: 6, urgency: 'medium', description: 'Perfecto para familia' },
    { size: '180 m²', price: 'S/ 24,000', monthly: 'S/ 900', available: 5, urgency: 'high', description: 'Espacioso y premium' },
    { size: '200 m²', price: 'S/ 30,000', monthly: 'S/ 1300', available: 2, urgency: 'high', description: 'Máximo confort' }
  ];

  // ✅ Lee ref del URL una sola vez: ?ref=XXXX o ?refId=XXXX
  const refCode = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return (params.get('ref') || params.get('refId') || '').trim();
    } catch {
      return '';
    }
  }, []);

  const normalizePhone = (v) => String(v || '').replace(/[^\d]/g, '');

  const validateLead = () => {
    const nombre = (lead.nombre || '').trim();
    const email = (lead.email || '').trim();
    const phone = normalizePhone(lead.telefono);

    if (!nombre || nombre.length < 3) return 'Ingresa un nombre válido (mín. 3 letras).';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Ingresa un correo válido.';
    if (!/^\d{9}$/.test(phone)) return 'El celular debe tener 9 dígitos.';
    return '';
  };

  // ✅ CLICK EN "Seleccionar Plan" => ABRE MODAL
  const openPlanModal = (plan) => {
    setErrorMsg('');
    setPlanError('');
    setPendingPlan(plan);
    setShowPlanModal(true);
  };

  // ✅ ENVÍO FINAL: resolve-ref con plan + lead
  const submitPlanRequest = async () => {
    if (sendingPlan) return; // evita doble click

    setPlanError('');
    const msg = validateLead();
    if (msg) {
      setPlanError(msg);
      return;
    }
    if (!pendingPlan) {
      setPlanError('No se encontró el plan seleccionado.');
      return;
    }

    setSendingPlan(true);

    try {
      const sourceUrl = window.location.href;

      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const anonKey =
        import.meta.env?.VITE_SUPABASE_ANON_KEY ||
        import.meta.env?.VITE_SUPABASE_PUBLISHABLE_KEY;

      if (!supabaseUrl || !anonKey) {
        throw new Error('Faltan variables de entorno de Supabase (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY).');
      }

      const url = `${supabaseUrl}/functions/v1/resolve-ref`;

      const payload = {
        refCode: refCode || '', // ✅ usa el memo (no re-leer params)
        sourceUrl,
        plan: {
          id: pendingPlan.id,
          name: pendingPlan.name,
          price: pendingPlan.price,
          originalPrice: pendingPlan.originalPrice,
          discount: pendingPlan.discount,
          monthlyEquivalent: pendingPlan.monthlyEquivalent,
          features: pendingPlan.features,
          project: 'Residencial 3 Volcanes'
        },
        lead: {
          nombre: (lead.nombre || '').trim(),
          email: (lead.email || '').trim(),
          telefono: normalizePhone(lead.telefono) // ✅ SIEMPRE dígitos
        }
      };

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: anonKey,
          // ✅ Edge Functions aceptan apikey; dejamos Authorization sólido también
          Authorization: `Bearer ${anonKey}`
        },
        body: JSON.stringify(payload)
      });

      const raw = await res.text();
      let data = null;
      try { data = JSON.parse(raw); } catch { data = null; }

      if (!res.ok) {
        const msgErr = data?.error || data?.message || raw || 'Error en resolve-ref';
        console.error('resolve-ref error:', data || raw);
        throw new Error(msgErr);
      }

      // ✅ Si hay whatsapp del referidor: abrimos WA
      if (data?.route === 'whatsapp_referrer' && data?.whatsapp) {
        const msgText = [
          `📌 *Nuevo interesado desde tu link de referido*`,
          ``,
          `👤 Nombre: ${(lead.nombre || '').trim()}`,
          `📧 Email: ${(lead.email || '').trim()}`,
          `📱 Celular: ${normalizePhone(lead.telefono)}`,
          ``,
          `🏡 Proyecto: Residencial 3 Volcanes`,
          `🧾 Plan: ${pendingPlan.name}`,
          `💰 Precio: ${pendingPlan.price} (${pendingPlan.originalPrice})`,
          `🏷️ Condición: ${pendingPlan.discount}`,
          `📈 Detalle: ${pendingPlan.monthlyEquivalent}`,
          ``,
          `🔗 Link: ${sourceUrl}`,
          refCode ? `🧩 Ref: ${refCode}` : ``
        ].filter(Boolean).join('\n');

        window.open(
          `https://wa.me/${data.whatsapp}?text=${encodeURIComponent(msgText)}`,
          '_blank',
          'noopener,noreferrer'
        );
      } else {
        alert('✅ Solicitud registrada. Un administrador te contactará pronto.');
      }

      // cerrar modal + limpiar
      setShowPlanModal(false);
      setPendingPlan(null);
      setLead({ nombre: '', email: '', telefono: '' });
    } catch (err) {
      console.error('Select plan error:', err);
      setPlanError(err?.message || 'Ocurrió un error al enviar la solicitud.');
    } finally {
      setSendingPlan(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }
  };

  return (
    <section className="py-16 sm:py-24 md:py-32 lg:py-40 bg-gradient-to-b from-white via-orange-50/30 to-white relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-orange-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-blue-200/30 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
          className="text-center mb-12 sm:mb-16 md:mb-20"
        >
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-100 to-red-100 border-2 border-orange-300 rounded-full mb-4 sm:mb-6 shadow-lg">
            <Icon name="DollarSign" size={20} className="text-orange-600" />
            <span className="text-xs sm:text-sm font-black text-orange-700 tracking-wide">PLANES DE INVERSIÓN FLEXIBLES</span>
          </motion.div>

          <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-black mb-4 sm:mb-6 tracking-tight">
            <span className="bg-gradient-to-r from-orange-600 via-red-600 to-orange-500 bg-clip-text text-transparent">
              Elige tu Plan Ideal
            </span>
          </motion.h2>

          <motion.p variants={itemVariants} className="text-base sm:text-lg md:text-xl text-gray-700 font-semibold max-w-3xl mx-auto px-4">
            Opciones de pago diseñadas para hacer realidad tu inversión inmobiliaria
          </motion.p>

          {errorMsg ? (
            <div className="mt-5 max-w-3xl mx-auto p-4 rounded-xl bg-red-50 border-2 border-red-200 text-red-700 font-semibold text-sm">
              {errorMsg}
            </div>
          ) : null}
        </motion.div>

        {/* Cards */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16 md:mb-20"
        >
          {plans?.map((plan) => (
            <motion.div
              key={plan?.id}
              variants={itemVariants}
              whileHover={{ y: -8, scale: 1.02 }}
              className={`relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-3 transition-all cursor-pointer touch-target shadow-xl ${
                plan?.recommended
                  ? 'border-orange-500 ring-4 ring-orange-200 shadow-2xl shadow-orange-200'
                  : 'border-gray-300 hover:border-orange-400 hover:shadow-2xl'
              } ${selectedPlan === plan?.id ? 'ring-4 ring-orange-300 scale-105' : ''}`}
              onClick={() => setSelectedPlan(plan?.id)}
            >
              {plan?.recommended && (
                <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-orange-600 to-red-600 px-4 sm:px-6 py-1.5 sm:py-2 rounded-full shadow-xl border-2 border-orange-300">
                    <span className="text-xs sm:text-sm font-black text-white flex items-center gap-1">
                      <Icon name="Star" size={16} className="fill-white" />
                      {plan?.badge}
                    </span>
                  </div>
                </div>
              )}

              <div className="text-center mb-6 sm:mb-8">
                <div className={`w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-2xl bg-gradient-to-br ${
                  plan?.color === 'success' ? 'from-emerald-500 to-green-600' : 'from-orange-500 to-red-600'
                } flex items-center justify-center shadow-xl`}>
                  <Icon name={plan?.icon} size={28} className="text-white" />
                </div>

                <h3 className="text-xl sm:text-2xl font-heading font-black text-gray-900 mb-2">{plan?.name}</h3>
                <p className="text-xs sm:text-sm text-gray-600 font-bold mb-4 sm:mb-6">{plan?.discount}</p>

                <div className="mb-2">
                  <span className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900">{plan?.price}</span>
                  <span className="text-sm sm:text-base text-gray-600 font-semibold ml-2">{plan?.originalPrice}</span>
                </div>
                <p className="text-xs sm:text-sm font-bold text-orange-600">{plan?.monthlyEquivalent}</p>
              </div>

              <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {plan?.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center mt-0.5 shadow-md">
                      <Icon name="Check" size={14} className="text-white font-bold" />
                    </div>
                    <span className="text-xs sm:text-sm text-gray-700 font-semibold flex-1">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openPlanModal(plan);
                }}
                variant={plan?.recommended ? 'default' : 'outline'}
                size="lg"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                className={`text-sm sm:text-base touch-target font-bold shadow-lg hover:shadow-xl transition-all ${
                  plan?.recommended
                    ? 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 border-2 border-orange-700'
                    : 'border-2 border-gray-300 hover:border-orange-500 hover:bg-orange-50 hover:text-orange-600'
                }`}
              >
                Seleccionar Plan
              </Button>
            </motion.div>
          ))}
        </motion.div>

        {/* Lot sizes */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={containerVariants}
          className="bg-gradient-to-br from-white to-orange-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 border-3 border-orange-200 shadow-2xl"
        >
          <div className="text-center mb-8 sm:mb-12">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-black text-gray-900 mb-3 sm:mb-4">
              Tamaños de Lote Disponibles
            </h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-700 font-semibold">
              Lotes de 150 a 200 m² - Precios desde S/ 15,000 hasta S/ 30,000
            </p>
            <p className="text-xs sm:text-sm text-orange-600 font-bold mt-2">
              Separación desde S/ 300 • Financiamiento hasta 5 años
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {lotSizes?.map((lot, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -4 }}
                className="relative bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-gray-300 hover:border-orange-500 hover:shadow-xl transition-all cursor-pointer touch-target"
              >
                {lot?.urgency === 'high' && (
                  <div className="absolute -top-2 -right-2">
                    <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white text-xs font-black px-2 sm:px-3 py-1 rounded-full animate-pulse shadow-xl border-2 border-white">
                      ¡ÚLTIMOS!
                    </div>
                  </div>
                )}

                <div className="text-center">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg">
                    <Icon name="Home" size={24} className="text-white" />
                  </div>

                  <h4 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">{lot?.size}</h4>
                  <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-3 sm:mb-4">{lot?.description}</p>

                  <div className="mb-3 sm:mb-4">
                    <p className="text-lg sm:text-xl md:text-2xl font-bold text-primary">{lot?.price}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Desde {lot?.monthly}/mes</p>
                  </div>

                  <div className="flex items-center justify-center gap-2 text-xs sm:text-sm">
                    <div className={`w-2 h-2 rounded-full ${
                      lot?.urgency === 'high' ? 'bg-error animate-pulse' : 'bg-success'
                    }`} />
                    <span className="text-muted-foreground">{lot?.available} disponibles</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              <div className="flex items-center gap-3 p-4 bg-background/50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="DollarSign" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-medium text-foreground">Separación</p>
                  <p className="text-xs text-muted-foreground">Desde S/ 300</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-background/50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Calendar" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-medium text-foreground">Financiamiento</p>
                  <p className="text-xs text-muted-foreground">Hasta 60 meses</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-background/50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Percent" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-medium text-foreground">Tasa Anual</p>
                  <p className="text-xs text-muted-foreground">8.5% fija</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* ✅ MODAL */}
      {showPlanModal && pendingPlan && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => {
            if (sendingPlan) return;
            setShowPlanModal(false);
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border-2 border-orange-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-xl font-black">Completa tus datos</h4>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-900"
                onClick={() => {
                  if (sendingPlan) return;
                  setShowPlanModal(false);
                }}
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Estás eligiendo: <span className="font-bold">{pendingPlan.name}</span>
            </p>

            <div className="space-y-3">
              <input
                className="w-full h-12 px-4 rounded-xl border-2 border-gray-300 focus:border-orange-500 outline-none"
                placeholder="Nombre completo"
                value={lead.nombre}
                onChange={(e) => setLead((p) => ({ ...p, nombre: e.target.value }))}
              />
              <input
                className="w-full h-12 px-4 rounded-xl border-2 border-gray-300 focus:border-orange-500 outline-none"
                placeholder="Correo"
                value={lead.email}
                onChange={(e) => setLead((p) => ({ ...p, email: e.target.value }))}
              />
              <input
                className="w-full h-12 px-4 rounded-xl border-2 border-gray-300 focus:border-orange-500 outline-none"
                placeholder="Celular (9 dígitos)"
                value={lead.telefono}
                onChange={(e) =>
                  setLead((p) => ({ ...p, telefono: normalizePhone(e.target.value) }))
                }
              />

              {planError && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                  {planError}
                </div>
              )}

              <Button
                type="button"
                fullWidth
                size="lg"
                disabled={sendingPlan}
                className="h-12 font-bold bg-gradient-to-r from-orange-600 to-red-600"
                onClick={submitPlanRequest}
              >
                {sendingPlan ? 'Enviando...' : 'Continuar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PricingSection;
